import { type EChartsExtensionInstallRegisters } from 'echarts';
import { util, graphic } from 'echarts/core';
import type GlobalModel from 'echarts/types/src/model/Global';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import type AxisModel from 'echarts/types/src/coord/cartesian/AxisModel';
import type CartesianAxisView from 'echarts/types/src/component/axis/CartesianAxisView';
import type { Group, Text } from 'zrender';
import { isFunction } from 'zrender/lib/core/util';
import { Z_LIST } from '../../theme/mobile/static/z';

/**
 * y轴分割线层级在series下
 * @param chart
 */
function parseYAxisSplitLineZ(chart: ExtensionAPI) {
  const yAxisModel = chart.getModel().getComponent('yAxis');
  if (!yAxisModel) {
    return;
  }
  const view = chart.getViewOfComponentModel(yAxisModel);
  view.group.childAt(0).eachChild(child => {
    if (child.anid?.includes('line')) {
      child.z = Z_LIST.splitLine;
    }
  });
}

const TWO = 2;

/**
 * 处理y轴最后一个坐标的位置
 */
function parseYAxisLabel(ecModel: GlobalModel, api: ExtensionAPI) {
  ecModel.eachComponent('yAxis', (compModel: AxisModel) => {
    if (!compModel?.get('show') || !compModel?.get('axisLabel')?.dvAlignEdge) {
      return;
    }

    const compView = api.getViewOfComponentModel(compModel) as CartesianAxisView;
    const labels: Text[] = [];

    // @ts-ignore
    (compView._axisGroup as Group).traverse(childEl => {
      if ((childEl.anid || '').startsWith('label')) {
        labels.push(childEl as Text);
      }
    });

    if (labels.length < TWO) {
      return;
    }

    const firstLabel = labels[0];
    const lastLabel = labels[labels.length - 1];
    const extentCoord = [
      compModel.axis.toGlobalCoord(compModel.axis.getExtent()[0]),
      compModel.axis.toGlobalCoord(compModel.axis.getExtent()[1])
    ];

    firstLabel.stopAnimation(null, true);
    firstLabel.attr({
      y: extentCoord[0],
      style: {
        verticalAlign: 'bottom',
        // 刻度值贴住了线，为了符合范式故设1px使字偏移 1px
        padding: [0, 0, 1, 0]
      }
    });

    lastLabel.stopAnimation(null, true);
    lastLabel.attr({
      y: extentCoord[1],
      style: {
        verticalAlign: 'top',
        // 刻度值贴住了线，为了符合范式故设1px使字往下走一点
        padding: [1, 0, 0, 0]
      }
    });

    // * 额外的逻辑
    // 解决标签重叠
    const firstChild = labels[0];
    const secondChild = labels[1];
    const secondLastChild = labels[labels.length - 2];
    const lastChild = labels[labels.length - 1];
    const secondChildSpan = secondChild._children.find(d => d.type === 'tspan');
    const secondLastChildSpan = secondLastChild._children.find(d => d.type === 'tspan');

    const firstChildRect = firstChild.getBoundingRect();
    const secondChildRect = secondChild.getBoundingRect();
    const secondLastChildRect = secondLastChild.getBoundingRect();
    const lastChildRect = lastChild.getBoundingRect();

    const headClip = graphic.clipRectByRect(
      {
        x: firstChild.x + firstChildRect.x,
        y: firstChild.y + firstChildRect.y,
        width: firstChildRect.width,
        height: firstChildRect.height
      },
      {
        x: secondChild.x + secondChildRect.x,
        y: secondChild.y + secondChildRect.y,
        width: secondChildRect.width,
        height: secondChildRect.height
      }
    );

    const tailClip = graphic.clipRectByRect(
      {
        x: lastChild.x + lastChildRect.x,
        y: lastChild.y + lastChildRect.y,
        width: lastChildRect.width,
        height: lastChildRect.height
      },
      {
        x: secondLastChild.x + secondLastChildRect.x,
        y: secondLastChild.y + secondLastChildRect.y,
        width: secondLastChildRect.width,
        height: secondLastChildRect.height
      }
    );

    if (headClip) {
      secondChildSpan.attr({
        style: { text: '' }
      });
    }

    if (tailClip) {
      secondLastChildSpan.attr({
        style: { text: '' }
      });
    }

    //
  });
}

// 自定义分割线样式
function customizeYAxisSplitLine(ecModel, api) {
  ecModel.eachComponent('yAxis', compModel => {
    const { option } = compModel;
    const compView = api.getViewOfComponentModel(compModel);
    const lines = [];
    // @ts-ignore
    (compView._axisGroup as Group).traverse(childEl => {
      if ((childEl.anid || '').startsWith('line_')) {
        lines.push(childEl as Text);
      }
    });

    // 自定义分割线样式
    if (isFunction(option?.splitLine?.lineStyle)) {
      lines.forEach((line, index) => {
        const { anid = '' } = line;
        const label = anid.split('_')[1];
        const lineStyle = { ...line.style };
        const customLineStyle = option.splitLine.lineStyle(index, label);
        util.merge(lineStyle, customLineStyle, true);
        line.attr({ style: lineStyle });
      });
    }

    // IOS16分割线样式异常补丁
    // TODO:缺少设备判断逻辑
    lines.forEach(line => {
      const { lineWidth } = line.style;
      line.attr({ style: { lineWidth: lineWidth * (1 + 0.1 * (0.5 + 0.5 * Math.random())) } });
    });
  });
}

export function yAxisExtensionInstall(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('afterupdate', (ecModel, api) => {
    parseYAxisSplitLineZ(api);
    parseYAxisLabel(ecModel, api);
    customizeYAxisSplitLine(ecModel, api);
  });
}
